import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Shield, Lock, Server, Key, AlertTriangle } from 'lucide-react';
import { Card } from '../components/ui/card';

const Security = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-pink-800 to-purple-900 pb-8">
      {/* Header */}
      <div className="sticky top-0 bg-purple-900/80 backdrop-blur-md border-b border-white/10 px-4 py-4 z-40">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="text-white p-2">
            <ChevronLeft className="w-6 h-6" />
          </button>
          <Shield className="w-6 h-6 text-pink-300" />
          <h1 className="text-xl font-bold text-white">Sécurité</h1>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6">
        <Card className="p-6 bg-white/10 backdrop-blur-md border-white/20">
          <div className="prose prose-invert max-w-none">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center">
                <Shield className="w-6 h-6 text-green-400" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white m-0">Votre sécurité, notre priorité</h2>
                <p className="text-pink-200 text-sm m-0">Découvrez comment nous protégeons vos données</p>
              </div>
            </div>

            {/* Security Features */}
            <div className="grid gap-4 mb-6">
              <div className="p-4 bg-white/5 rounded-lg border border-white/10">
                <div className="flex items-center gap-3 mb-2">
                  <Lock className="w-5 h-5 text-blue-400" />
                  <h3 className="text-lg font-semibold text-white m-0">Chiffrement des Données</h3>
                </div>
                <p className="text-pink-100 text-sm">
                  Toutes vos données sont chiffrées en transit via HTTPS/TLS et vos mots de passe 
                  sont protégés par un algorithme de hachage bcrypt.
                </p>
              </div>

              <div className="p-4 bg-white/5 rounded-lg border border-white/10">
                <div className="flex items-center gap-3 mb-2">
                  <Server className="w-5 h-5 text-purple-400" />
                  <h3 className="text-lg font-semibold text-white m-0">Serveurs Sécurisés</h3>
                </div>
                <p className="text-pink-100 text-sm">
                  Nos serveurs sont hébergés dans des centres de données certifiés avec des mesures 
                  de sécurité physiques et logiques avancées.
                </p>
              </div>

              <div className="p-4 bg-white/5 rounded-lg border border-white/10">
                <div className="flex items-center gap-3 mb-2">
                  <Key className="w-5 h-5 text-yellow-400" />
                  <h3 className="text-lg font-semibold text-white m-0">Authentification Sécurisée</h3>
                </div>
                <p className="text-pink-100 text-sm">
                  Système d'authentification JWT avec tokens sécurisés et sessions limitées dans le temps 
                  pour protéger votre compte.
                </p>
              </div>
            </div>

            <h2 className="text-xl font-bold text-white mt-6 mb-3">Mesures de Sécurité Techniques</h2>
            <ul className="list-disc list-inside text-pink-100 text-sm mb-4 space-y-2">
              <li><strong>HTTPS obligatoire</strong> - Toutes les communications sont chiffrées</li>
              <li><strong>Protection CORS</strong> - Contrôle strict des origines autorisées</li>
              <li><strong>Validation des entrées</strong> - Protection contre les injections</li>
              <li><strong>Rate limiting</strong> - Protection contre les attaques par force brute</li>
              <li><strong>Mises à jour régulières</strong> - Correctifs de sécurité appliqués rapidement</li>
            </ul>

            <h2 className="text-xl font-bold text-white mt-6 mb-3">Protection de Vos Données de Santé</h2>
            <p className="text-pink-100 text-sm leading-relaxed mb-4">
              Vos données de santé (cycle, contraception, symptômes) bénéficient d'une protection renforcée :
            </p>
            <ul className="list-disc list-inside text-pink-100 text-sm mb-4 space-y-2">
              <li>Stockage séparé et chiffré</li>
              <li>Accès limité au strict nécessaire</li>
              <li>Journalisation des accès pour audit</li>
              <li>Anonymisation pour les statistiques globales</li>
            </ul>

            <h2 className="text-xl font-bold text-white mt-6 mb-3">Bonnes Pratiques pour Vous</h2>
            <div className="p-4 bg-yellow-500/20 rounded-lg border border-yellow-400/30 mb-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-yellow-400 mt-0.5" />
                <div>
                  <h4 className="text-white font-semibold m-0 mb-2">Protégez votre compte</h4>
                  <ul className="text-yellow-100 text-sm space-y-1 m-0 p-0 list-none">
                    <li>• Utilisez un mot de passe unique et complexe</li>
                    <li>• Ne partagez jamais vos identifiants</li>
                    <li>• Déconnectez-vous sur les appareils partagés</li>
                    <li>• Signalez toute activité suspecte</li>
                  </ul>
                </div>
              </div>
            </div>

            <h2 className="text-xl font-bold text-white mt-6 mb-3">Signaler un Problème de Sécurité</h2>
            <p className="text-pink-100 text-sm leading-relaxed mb-4">
              Si vous découvrez une vulnérabilité ou un problème de sécurité, contactez-nous immédiatement :
            </p>
            <div className="p-4 bg-red-500/20 rounded-lg border border-red-400/30">
              <p className="text-white text-sm m-0">
                <strong>Email sécurité :</strong> <span className="text-pink-300">support@fleurde-vie.com</span><br />
                Nous nous engageons à traiter tous les signalements sous 48h.
              </p>
            </div>

            <div className="mt-8 p-4 bg-green-500/20 rounded-lg border border-green-400/30">
              <div className="flex items-center gap-2 mb-2">
                <Shield className="w-5 h-5 text-green-400" />
                <span className="text-white font-semibold">Certifications et Conformité</span>
              </div>
              <p className="text-green-100 text-sm">
                Fleur de Vie respecte les standards de sécurité internationaux et les réglementations 
                locales en matière de protection des données de santé.
              </p>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default Security;
